import { useState } from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import InputBase from '@mui/material/InputBase';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import LinearProgress from '@mui/material/LinearProgress';
import { AddCredits, SetCreditsThreshold } from 'services/openapiClient';
import { useTranslation } from 'react-i18next';

const PREFIX = 'CreditUpdateWidget';

const classes = {
  root: `${PREFIX}-root`,
  addCredit: `${PREFIX}-addCredit`,
  button: `${PREFIX}-button`,
};

const Root = styled('div')(({ theme }) => ({
  width: 400,
  margin: theme.spacing(1),
  padding: theme.spacing(1),
  paddingBottom: theme.spacing(3),
  border: '1px solid #666',

  [`& .${classes.addCredit}`]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    textAlign: 'right',
    border: '1px solid #666',
  },
  [`& .${classes.button}`]: {
    margin: theme.spacing(1),
    marginTop: theme.spacing(3),
    textAlign: 'right',
  },
}));

interface IProps {
  organisationId: string;
  remainingCredits: number;
  thresholdCredits: number;
  onUpdateOrganisation: () => void;
}

function CreditUpdateWidget(props: IProps): JSX.Element {
  const { organisationId, remainingCredits, thresholdCredits, onUpdateOrganisation } = props;
  const [overallCredits, setOverallCredits] = useState(remainingCredits);
  const [numberOfCreditsToAdd, setNumberOfCreditsToAdd] = useState(0);
  const [thresholdLimit, setThresholdLimit] = useState(thresholdCredits);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('default');

  async function handleUpdateCredit() {
    if (!organisationId) {
      setError('OrganisationId is missing');
      return;
    }
    if (!numberOfCreditsToAdd) {
      setError('None Zero value required');
      return;
    }
    setLoading(true);
    try {
      await AddCredits({
        organisationId,
        numberOfCreditsToAdd,
      });
      setOverallCredits(overallCredits + numberOfCreditsToAdd);
      onUpdateOrganisation();
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  }

  async function handleUpdateLimit() {
    if (!organisationId) {
      setError('OrganisationId is missing');
      return;
    }
    setLoading(true);
    try {
      await SetCreditsThreshold({
        organisationId,
        thresholdLimit,
      });
      onUpdateOrganisation();
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  }

  const handleChangeCredit = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNumberOfCreditsToAdd(parseInt(event.target.value));
  };

  const handleCreditThresholdLimit = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setThresholdLimit(parseInt(event.target.value));
  };

  return (
    <Root>
      {loading && <LinearProgress />}
      {error && (
        <Snackbar open={true} autoHideDuration={5000}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}
      <Stack direction="column" spacing={1}>
        <div
          style={{
            display: 'flex',
            fontWeight: 'bold',
            lineHeight: '40px',
          }}
        >
          <label
            style={{
              width: '50%',
              paddingLeft: '5px',
              fontSize: '14px',
              color: '#666666',
            }}
          >
            {t('credits-remaining')}:
          </label>
          <div style={{ flex: 1 }}>{overallCredits}</div>
        </div>
        <div
          style={{
            display: 'flex',
            lineHeight: '40px',
          }}
        >
          <label
            style={{
              width: '50%',
              paddingLeft: '5px',
              fontSize: '14px',
              color: '#666666',
            }}
          >
            {t('add-credits')}
          </label>
          <div style={{ flex: 1 }}>
            <FormControl error={Boolean(error)}>
              <InputBase
                disabled={loading}
                className={classes.addCredit}
                placeholder="1"
                onChange={handleChangeCredit}
                inputProps={{ type: 'number', 'aria-label': 'add credit' }}
              />
            </FormControl>
          </div>
        </div>
        <div className={classes.button}>
          <Button
            variant="contained"
            disabled={loading}
            color="primary"
            autoFocus
            onClick={handleUpdateCredit}
          >
            {t('update-credits')}
          </Button>
        </div>
        <div
          style={{
            display: 'flex',
            fontWeight: 'bold',
            lineHeight: '40px',
          }}
        >
          <label
            style={{
              width: '50%',
              paddingLeft: '5px',
              fontSize: '14px',
              color: '#666666',
            }}
          >
            {t('credits-threshold-limit')}:
          </label>
          <div style={{ flex: 1 }}>{thresholdCredits}</div>
        </div>
        <div
          style={{
            display: 'flex',
            lineHeight: '40px',
          }}
        >
          <label
            style={{
              width: '50%',
              paddingLeft: '5px',
              fontSize: '14px',
              color: '#666666',
            }}
          >
            {t('new-limit')}:
          </label>
          <div style={{ flex: 1 }}>
            <FormControl error={Boolean(error)}>
              <InputBase
                disabled={loading}
                className={classes.addCredit}
                placeholder={thresholdCredits.toString()}
                onChange={handleCreditThresholdLimit}
                inputProps={{ type: 'number', 'aria-label': 'add credit' }}
              />
            </FormControl>
          </div>
        </div>
        <div className={classes.button}>
          <Button
            variant="contained"
            disabled={loading}
            color="primary"
            autoFocus
            onClick={handleUpdateLimit}
          >
            {t('update-limit')}
          </Button>
        </div>
      </Stack>
    </Root>
  );
}

export default CreditUpdateWidget;
